import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FiEdit2, FiX } from 'react-icons/fi';
import styled from 'styled-components';
import media from '../../../../../constants/media';
import { IFormSelectOption } from '../../../../../interfaces/forms/forms';
import FormikFieldWrapper from '../../../../base/forms/formikFieldWrapper';
import FormikSelect from '../../../../base/forms/formikSelect';
import { Input } from '../../../../base/forms/input';
import { IStepExportFormInfo } from '../stepExport.schema';
import { computeLineValue } from '../stepExportForm/helpers';

type ContentLine = IStepExportFormInfo['contents'][number];

interface Props {
	index: number;
	forcedDocuments: boolean;
	isUSAorPR: boolean;
	countries: IFormSelectOption[];
	shouldHidePrice: boolean;
	showWeight: boolean;
	openLookup: () => void;
	removeItem: () => void;
}

/**
 * A single customs item rendered as a card: a header with the HS code and item description
 * (edit/remove buttons on the right), then the item's country of origin, quantity, value and
 * weight, with the line totals below.
 */
const CustomsItem: FC<Props> = ({
	index,
	forcedDocuments,
	countries,
	shouldHidePrice,
	showWeight,
	openLookup,
	removeItem,
}) => {
	const { t } = useTranslation('registerShipment');
	const { values, errors, touched, setFieldValue, setFieldTouched } = useFormikContext<IStepExportFormInfo>();
	const line = values.contents[index];
	const currencyCode = values.currency?.value;
	const itemErrors = errors.contents?.[index] as FormikErrors<ContentLine> | undefined;
	const itemTouched = touched.contents?.[index] as FormikTouched<ContentLine> | undefined;

	// A total is shown only when its inputs are filled in and none of them shows an error
	const hasVisibleError = (field: keyof ContentLine) => !!(itemErrors?.[field] && itemTouched?.[field]);
	const totalValue =
		hasVisibleError('unitPrice') || hasVisibleError('amountInPackage')
			? ''
			: computeLineValue(line.unitPrice, line.amountInPackage);
	const totalWeight =
		hasVisibleError('weight') || hasVisibleError('amountInPackage')
			? ''
			: computeLineValue(line.weight, line.amountInPackage);

	return (
		<Card>
			<Header>
				<HeaderTitle>
					<HsCode>{line.hsTariffCode}</HsCode> - {line.contents}
				</HeaderTitle>
				{!forcedDocuments && (
					<HeaderActions>
						<HeaderIconButton
							type="button"
							title={t('step-export.hsCodeLookup.editHsCode')}
							aria-label={t('step-export.hsCodeLookup.editHsCode')}
							onClick={openLookup}>
							<FiEdit2 />
						</HeaderIconButton>
						<HeaderIconButton
							$iconSize="2.8rem"
							type="button"
							title={t('step-export.hsCodeLookup.removeItem')}
							aria-label={t('step-export.hsCodeLookup.removeItem')}
							onClick={removeItem}>
							<FiX />
						</HeaderIconButton>
					</HeaderActions>
				)}
			</Header>
			<Body>
				<ValuesSection>
					<ValuesGrid $columns={shouldHidePrice ? 2 : showWeight ? 4 : 3}>
						<FormikFieldWrapper
							label={`${t('step-export.chooseCountry')} *`}
							name={`contents.${index}.countryOfOrigin`}
							smallLabel
							languageComponent="registerShipment"
							reserveErrorSpace>
							<FormikSelect
								id={`contents.${index}.countryOfOrigin`}
								inputId={`contents.${index}.countryOfOrigin-input`}
								name={`contents.${index}.countryOfOrigin`}
								options={countries}
								value={line.countryOfOrigin}
								disabled={forcedDocuments}
								onChange={setFieldValue}
								onBlur={setFieldTouched}
								placeholder={t('step-export.countryPlaceholder')}
							/>
						</FormikFieldWrapper>
						<FormikFieldWrapper
							label={`${t('step-export.quantity')} *`}
							name={`contents.${index}.amountInPackage`}
							smallLabel
							languageComponent="registerShipment"
							reserveErrorSpace>
							<Input
								id={`contents.${index}.amountInPackage`}
								name={`contents.${index}.amountInPackage`}
								value={line.amountInPackage}
								inputMode="numeric"
								maxLength={3}
								disabled={forcedDocuments}
								onKeyPress={e => {
									// Quantity is a whole number of items, so only allow digits (no commas, dots, etc.)
									if (!/[0-9]/.test(e.key)) e.preventDefault();
								}}
								onChange={e =>
									setFieldValue(
										`contents.${index}.amountInPackage`,
										e.target.value.replace(/\D/g, '').slice(0, 3)
									)
								}
								onBlur={() => setFieldTouched(`contents.${index}.amountInPackage`, true)}
								placeholder={t('step-export.quantityPlaceholder')}
							/>
						</FormikFieldWrapper>
						{!shouldHidePrice && (
							<FormikFieldWrapper
								label={`${t('step-export.unit-price')} *`}
								name={`contents.${index}.unitPrice`}
								smallLabel
								languageComponent="registerShipment"
								reserveErrorSpace>
								<InputWithSuffixWrapper>
									<Input
										id={`contents.${index}.unitPrice`}
										name={`contents.${index}.unitPrice`}
										value={line.unitPrice}
										inputMode="decimal"
										maxLength={10}
										disabled={forcedDocuments}
										onKeyPress={e => {
											// Unit price allows digits and a comma decimal separator only.
											if (!/[0-9,]/.test(e.key)) e.preventDefault();
										}}
										onChange={e =>
											setFieldValue(
												`contents.${index}.unitPrice`,
												e.target.value.replace(/[^0-9,]/g, '')
											)
										}
										onBlur={() => setFieldTouched(`contents.${index}.unitPrice`, true)}
										placeholder={t('step-export.unit-price')}
									/>
									{currencyCode && <InputSuffix>{currencyCode}</InputSuffix>}
								</InputWithSuffixWrapper>
							</FormikFieldWrapper>
						)}
						{showWeight && (
							<FormikFieldWrapper
								label={`${t('step-export.weight')} *`}
								name={`contents.${index}.weight`}
								smallLabel
								languageComponent="registerShipment"
								reserveErrorSpace>
								<InputWithSuffixWrapper>
									<Input
										id={`contents.${index}.weight`}
										name={`contents.${index}.weight`}
										value={line.weight}
										inputMode="decimal"
										maxLength={6}
										onKeyPress={e => {
											// Weight allows digits and one comma separator; a period types as a comma.
											const isSeparator = e.key === ',' || e.key === '.';
											if (!/[0-9,.]/.test(e.key) || (isSeparator && line.weight?.includes(','))) {
												e.preventDefault();
											}
										}}
										onChange={e => {
											const cleaned = e.target.value.replace(/\./g, ',').replace(/[^0-9,]/g, '');
											const firstComma = cleaned.indexOf(',') + 1;
											setFieldValue(
												`contents.${index}.weight`,
												cleaned.slice(0, firstComma) + cleaned.slice(firstComma).replace(/,/g, '')
											);
										}}
										onBlur={() => setFieldTouched(`contents.${index}.weight`, true)}
										placeholder={t('step-export.weight')}
									/>
									<InputSuffix>kg</InputSuffix>
								</InputWithSuffixWrapper>
							</FormikFieldWrapper>
						)}
						{!shouldHidePrice && (
							<Totals>
								<TotalBlock>
									<TotalLabel>{t('step-export.total-price')}</TotalLabel>
									<TotalAmount data-testid={`contents.${index}.value`}>
										{totalValue || '—'}
										<TotalUnit $hidden={!totalValue}>{currencyCode}</TotalUnit>
									</TotalAmount>
								</TotalBlock>
								{showWeight && (
									<TotalBlock>
										<TotalLabel>{t('step-export.total-weight')}</TotalLabel>
										<TotalAmount data-testid={`contents.${index}.weightValue`}>
											{totalWeight || '—'}
											<TotalUnit $hidden={!totalWeight}>kg</TotalUnit>
										</TotalAmount>
									</TotalBlock>
								)}
							</Totals>
						)}
					</ValuesGrid>
				</ValuesSection>
			</Body>
		</Card>
	);
};

export default CustomsItem;

// The outline is a shadow ring (no border), like HsCodeLookup, so switching to the focus ring doesn't shift anything
const Card = styled.div`
	border-radius: 8px;
	box-shadow: 0 0 0 1px ${({ theme }) => theme.colorsV2.grey5}, 0 4px 18px rgba(43, 43, 51, 0.05);
	box-sizing: border-box;

	&:focus-within {
		box-shadow: 0 0 0 4px ${({ theme }) => theme.colorsV2.mainPink}4d;
	}
`;

const Header = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto;
	align-items: center;
	gap: 1.6rem;
	padding: 1.5rem 2.8rem;
	border-radius: 8px 8px 0 0;
	background-color: ${({ theme }) => theme.colorsV2.mainPink}1a;
	border-bottom: 1px solid ${({ theme }) => theme.colorsV2.mainPink}4d;

	${media.mobileOnly`
		padding: 1.5rem 1.6rem;
	`}
`;

const HeaderTitle = styled.b`
	display: -webkit-box;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	overflow: hidden;
	font-size: 1.7rem;
	font-family: ${({ theme }) => theme.fonts.ArimoBold};
	color: ${({ theme }) => theme.colors.black};
`;

const HsCode = styled.span`
	color: ${({ theme }) => theme.colorsV2.mainPink};
`;

const HeaderActions = styled.div`
	display: flex;
	align-items: center;
	gap: 1.2rem;
`;

// Fixed-size box so the hover circle is the same size whatever the icon size
const HeaderIconButton = styled.button<{ $iconSize?: string }>`
	position: relative;
	isolation: isolate;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 2.8rem;
	height: 2.8rem;
	padding: 0;
	border: none;
	background: none;
	color: ${({ theme }) => theme.colorsV2.mainPink};
	cursor: pointer;

	svg {
		width: ${({ $iconSize }) => $iconSize ?? '2.4rem'};
		height: ${({ $iconSize }) => $iconSize ?? '2.4rem'};
	}

	&::before {
		content: '';
		position: absolute;
		inset: -0.4rem;
		border-radius: 50%;
		background-color: transparent;
		z-index: -1;
	}
	&:hover::before,
	&:focus-visible::before {
		background-color: ${({ theme }) => theme.colorsV2.mainPink};
	}
	&:hover,
	&:focus-visible {
		color: ${({ theme }) => theme.colors.white};
	}
	&:focus-visible {
		outline: none;
	}
`;

const Body = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	padding: 2.4rem;
`;

const ValuesSection = styled.div`
	${media.mobileOnly`
		padding: 2rem 1.6rem;
	`}
`;

const ValuesGrid = styled.div<{ $columns: number }>`
	display: grid;
	grid-template-columns: ${({ $columns }) => `repeat(${$columns}, minmax(0, 1fr))`};
	column-gap: 3.2rem;
	align-items: start;

	${media.mobileOnly`
		grid-template-columns: 1fr;
	`}
`;

const InputWithSuffixWrapper = styled.div`
	position: relative;
	width: 100%;
`;

const InputSuffix = styled.span`
	position: absolute;
	right: 1.5rem;
	top: 50%;
	transform: translateY(-50%);
	color: ${({ theme }) => theme.colors.gray};
	font-size: 1.5rem;
	pointer-events: none;
	user-select: none;
`;

const Totals = styled.div`
	grid-column: 1 / -1;
	display: flex;
	justify-content: flex-end;
	gap: 3.2rem;
`;

const TotalBlock = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	text-align: right;
`;

const TotalLabel = styled.span`
	font-size: 1.4rem;
	font-family: ${({ theme }) => theme.fonts.ArimoMedium};
	color: ${({ theme }) => theme.colors.grayDarker};
`;

const TotalAmount = styled.span`
	display: flex;
	align-items: baseline;
	gap: 0.6rem;
	font-size: 2.2rem;
	line-height: 1;
	color: ${({ theme }) => theme.colorsV2.mainPink};
`;

// Always rendered (hidden when there's no total) so the line height doesn't change
const TotalUnit = styled.span<{ $hidden: boolean }>`
	font-size: 1.6rem;
	line-height: 1;
	color: ${({ theme }) => theme.colors.gray};
	visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
`;
